import type {
  BillingRequirement,
  EvidenceStatus,
  RequirementEvidenceStatus,
  TestEvidence,
} from "./types";

/**
 * Presentation rules for the charge-code board.
 *
 * Two independent facts describe every code and the board must never conflate
 * them:
 *
 * - `certification` is the reviewed human judgement recorded in the billing
 *   BRD — is this rule believed correct and complete?
 * - `evidenceStatus` is what automation observed on the current build — did a
 *   test that claims this code actually run and pass?
 *
 * A code can be `certified` with no automated evidence, and a code can have a
 * green test while its rule is still an open `gap`. Collapsing the two into one
 * badge is exactly the ambiguity QA has been asking us to remove.
 */

export const requirementStatusLabel: Record<RequirementEvidenceStatus, string> = {
  passed: "Proven on this build",
  flaky: "Passed only on retry",
  failed: "Failing",
  stale: "Proven on an older build",
  missing: "Not run",
  fixme: "Known gap",
  skipped: "Skipped",
  unlinked: "No automated test",
};

/** Worst-first, so the codes needing attention sort to the top of a family. */
const boardPriority: RequirementEvidenceStatus[] = [
  "failed",
  "flaky",
  "stale",
  "unlinked",
  "missing",
  "fixme",
  "skipped",
  "passed",
];

export function requirementStatus(requirement: BillingRequirement): RequirementEvidenceStatus {
  return requirement.evidenceStatus ?? "unlinked";
}

/**
 * True when automation currently backs this code on the exact build. Only a
 * clean pass counts — a retry pass and an older-build pass are both reported
 * as their own state rather than folded into green.
 */
export function isProven(requirement: BillingRequirement): boolean {
  return requirementStatus(requirement) === "passed";
}

export interface RequirementSummary {
  total: number;
  proven: number;
  attention: number;
  unlinked: number;
  openGaps: number;
}

export function summarizeRequirements(requirements: BillingRequirement[]): RequirementSummary {
  return {
    total: requirements.length,
    proven: requirements.filter(isProven).length,
    attention: requirements.filter(needsAttention).length,
    unlinked: requirements.filter((item) => requirementStatus(item) === "unlinked").length,
    openGaps: requirements.filter((item) => item.certification === "gap").length,
  };
}

export interface RequirementFamily {
  name: string;
  requirements: BillingRequirement[];
  proven: number;
  /** Failing, flaky, or proven only on a superseded build. */
  attention: number;
  /** Reviewed codes that no automated test claims at all. */
  unlinked: number;
}

/** True when automation currently says something is wrong with this code. */
export function needsAttention(requirement: BillingRequirement): boolean {
  return ["failed", "flaky", "stale"].includes(requirementStatus(requirement));
}

export function groupByFamily(requirements: BillingRequirement[]): RequirementFamily[] {
  const families = new Map<string, BillingRequirement[]>();
  for (const requirement of requirements) {
    const name = requirement.family || "Other";
    families.set(name, [...(families.get(name) ?? []), requirement]);
  }
  return Array.from(families.entries())
    .map(([name, items]) => ({
      name,
      proven: items.filter(isProven).length,
      attention: items.filter(needsAttention).length,
      unlinked: items.filter((item) => requirementStatus(item) === "unlinked").length,
      requirements: [...items].sort(
        (left, right) =>
          boardPriority.indexOf(requirementStatus(left)) -
            boardPriority.indexOf(requirementStatus(right)) ||
          left.code.localeCompare(right.code),
      ),
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * Decisive-first, matching the API's requirement ranking: an affirmative pass
 * outranks the absence of a re-run, because most evidence sits outside the
 * smoke selection and routinely carries an older SHA.
 */
const decisiveOrder: EvidenceStatus[] = [
  "failed",
  "flaky",
  "passed",
  "stale",
  "missing",
  "fixme",
  "skipped",
];

function decisiveStatus(statuses: EvidenceStatus[]): EvidenceStatus {
  return decisiveOrder.find((status) => statuses.includes(status)) ?? "missing";
}

/**
 * One reference the reviewed BRD names as proof of a charge code, resolved
 * against the tests the Hub can actually see.
 */
export interface DeclaredEvidence {
  ref: string;
  kind: "pytest" | "playwright" | "other";
  /** The module or requirement the reference points at, for display. */
  target: string;
  /** False when nothing in the published catalogs matches the reference. */
  resolved: boolean;
  testCount: number;
  passing: number;
  status?: EvidenceStatus;
}

/**
 * Resolve the evidence the billing BRD *claims* proves a code.
 *
 * The board's badge is built the other way round — from tests that annotate
 * the codes they exercise — because that link is the dependable one. But the
 * reviewed ground truth also names its own evidence (`pytest:test_cms_floor_guard`),
 * and silently ignoring those names hides two different failures: a reference
 * that no longer points at any test, and a reference that points at a suite
 * whose results never reach the Hub. Both mean a code the BRD calls certified
 * is not actually being proved, so both must be visible rather than absent.
 */
export function resolveDeclaredEvidence(
  refs: string[],
  tests: TestEvidence[],
): DeclaredEvidence[] {
  return refs.map((ref) => {
    const [prefix, ...rest] = ref.split(":");
    const target = rest.join(":");
    const kind: DeclaredEvidence["kind"] =
      prefix === "pytest" ? "pytest" : prefix === "playwright" ? "playwright" : "other";
    const matches =
      kind === "pytest"
        ? tests.filter((item) => item.path.endsWith(`/${target}.py`))
        : kind === "playwright"
          ? tests.filter(
              (item) =>
                (item.testType ?? "playwright") === "playwright" &&
                (item.requirementIds ?? []).includes(target),
            )
          : [];
    return {
      ref,
      kind,
      target: target || ref,
      resolved: matches.length > 0,
      testCount: matches.length,
      passing: matches.filter((item) => item.status === "passed").length,
      status: matches.length > 0 ? decisiveStatus(matches.map((item) => item.status)) : undefined,
    };
  });
}

export function filterRequirements(
  requirements: BillingRequirement[],
  query: string,
  family: string,
): BillingRequirement[] {
  const needle = query.trim().toLocaleLowerCase();
  return requirements.filter((item) => {
    const haystack = `${item.code} ${item.title} ${item.rule} ${item.family} ${item.id}`.toLocaleLowerCase();
    return (!needle || haystack.includes(needle)) && (family === "all" || item.family === family);
  });
}
